import React, { useEffect, useState } from "react";
import { Container, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, Pagination, Alert, CircularProgress } from "@mui/material";
import { useAuth } from "../../../context/AuthContext";

const LOG_TYPES = [
    { value: "system", label: "Sistema", allowedRoles: ["SUPER_ADMIN", "SYSTEM_ADMIN"] },
    { value: "audit", label: "Auditoría", allowedRoles: ["SUPER_ADMIN", "SYSTEM_ADMIN", "BUSINESS_ADMIN", "ADMIN"] },
    { value: "finance", label: "Finanzas", allowedRoles: ["SUPER_ADMIN", "SYSTEM_ADMIN", "BUSINESS_ADMIN", "ADMIN"] },
    { value: "business", label: "Negocio", allowedRoles: ["SUPER_ADMIN", "BUSINESS_ADMIN", "ADMIN"] }
];
const LOG_FILES = ["error", "info", "warning", "actions", "access", "transactions", "balance", "payout", "changes", "updates", "kyc", "aml", "export", "admin-actions"];

const PAGE_SIZE = 50;

const SystemLogs = () => {
    const { userProfile } = useAuth();
    const [logType, setLogType] = useState("system");
    const [logFile, setLogFile] = useState("error");
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Verificar acceso según rol
    const userRole = userProfile?.role?.toUpperCase() || "USER";
    const allowedTypes = LOG_TYPES.filter(t => t.allowedRoles.includes(userRole));
    const canView = allowedTypes.some(t => t.value === logType);

    useEffect(() => {
        if (!canView) return;
        setLoading(true);
        setError("");
        fetch(`/api/admin/logs?type=${logType}&file=${logFile}&page=${page}&pageSize=${PAGE_SIZE}`)
            .then(res => {
                if (!res.ok) throw new Error("No autorizado o error de backend");
                return res.json();
            })
            .then(data => {
                setLogs(data.logs || []);
                setTotal(data.total || 0);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [logType, logFile, page, canView]);

    if (!canView) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">No tienes permisos para ver estos logs.</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" color="primary" sx={{ mb: 3 }}>
                Logs del Sistema
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl>
                    <InputLabel>Tipo de Log</InputLabel>
                    <Select value={logType} label="Tipo de Log" onChange={e => { setLogType(e.target.value); setPage(1); }}>
                        {allowedTypes.map(type => (
                            <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl>
                    <InputLabel>Archivo</InputLabel>
                    <Select value={logFile} label="Archivo" onChange={e => { setLogFile(e.target.value); setPage(1); }}>
                        {LOG_FILES.map(file => (
                            <MenuItem key={file} value={file}>{file}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Level/Action</TableCell>
                                    <TableCell>Service</TableCell>
                                    <TableCell>Mensaje/Detalles</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 ? (
                                    <TableRow><TableCell colSpan={4} align="center">No hay logs para mostrar.</TableCell></TableRow>
                                ) : logs.map((log, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{log.timestamp || "-"}</TableCell>
                                        <TableCell>{log.level || log.action || log.eventType || "-"}</TableCell>
                                        <TableCell>{log.service || "-"}</TableCell>
                                        <TableCell>
                                            {log.message || log.details || log.raw || JSON.stringify(log)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination
                            count={Math.ceil(total / PAGE_SIZE)}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                </>
            )}
        </Container>
    );
};

export default SystemLogs; 